import { useCallback, useState } from "react";

export interface MenuPosition {
  top: number;
  left: number;
}

interface UseMenuPositionOptions {
  /** Estimated width of the menu for viewport clamping (default: 180) */
  menuWidth?: number;
  /** Estimated height of the menu for viewport clamping (default: 140) */
  menuHeight?: number;
  /** Gap between trigger element and menu (default: 5) */
  offset?: number;
  /** Minimum distance from viewport edge (default: 8) */
  padding?: number;
}

/**
 * Hook for positioning a dropdown/context menu relative to a trigger element,
 * clamped to viewport boundaries.
 *
 * Returns the current position (or null if closed) and handlers to open/close/toggle.
 */
export function useMenuPosition(options: UseMenuPositionOptions = {}) {
  const { menuWidth = 180, menuHeight = 140, offset = 5, padding = 8 } = options;
  const [position, setPosition] = useState<MenuPosition | null>(null);

  const open = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      let top = rect.bottom + offset;
      let left = rect.right - menuWidth;

      // Clamp to viewport boundaries
      if (left < padding) left = padding;
      if (left + menuWidth > window.innerWidth - padding) {
        left = window.innerWidth - menuWidth - padding;
      }
      if (top + menuHeight > window.innerHeight - padding) {
        top = rect.top - menuHeight - offset;
      }

      setPosition({ top, left });
    },
    [menuWidth, menuHeight, offset, padding],
  );

  const close = useCallback(() => {
    setPosition(null);
  }, []);

  const toggle = useCallback(
    (e: React.MouseEvent<HTMLElement>, isCurrentlyOpen: boolean) => {
      if (isCurrentlyOpen) {
        close();
      } else {
        open(e);
      }
    },
    [open, close],
  );

  return { position, open, close, toggle };
}
